import { useCallback, useEffect, useRef, useState } from 'react';

const BREAKPOINT_MD = 768;
const BREAKPOINT_XL = 1280;

function getItemsPerView(width: number): number {
    if (width >= BREAKPOINT_XL) return 3;
    if (width >= BREAKPOINT_MD) return 2;
    return 1;
}

function prefersReducedMotion(): boolean {
    return (
        typeof window !== 'undefined' &&
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
}

export interface UseVacationCarouselOptions {
    itemCount: number;
}

export interface UseVacationCarouselResult {
    trackRef: React.RefObject<HTMLDivElement | null>;
    page: number;
    pageCount: number;
    itemsPerView: number;
    canPrev: boolean;
    canNext: boolean;
    hasOverflow: boolean;
    next: () => void;
    prev: () => void;
    goToPage: (page: number) => void;
    handleKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void;
}

export function useVacationCarousel({
    itemCount,
}: UseVacationCarouselOptions): UseVacationCarouselResult {
    const trackRef = useRef<HTMLDivElement>(null);
    const [itemsPerView, setItemsPerView] = useState(() =>
        getItemsPerView(typeof window === 'undefined' ? BREAKPOINT_XL : window.innerWidth)
    );
    const [page, setPage] = useState(0);

    const pageCount = Math.max(1, Math.ceil(itemCount / itemsPerView));
    const hasOverflow = itemCount > itemsPerView;

    useEffect(() => {
        const handleResize = () => setItemsPerView(getItemsPerView(window.innerWidth));
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        setPage((current) => Math.min(current, pageCount - 1));
    }, [pageCount]);

    const scrollToPage = useCallback((target: number) => {
        const track = trackRef.current;
        if (!track) return;

        track.scrollTo({
            left: track.clientWidth * target,
            behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        });
    }, []);

    const goToPage = useCallback(
        (target: number) => {
            const clamped = Math.min(Math.max(target, 0), pageCount - 1);
            setPage(clamped);
            scrollToPage(clamped);
        },
        [pageCount, scrollToPage]
    );

    const next = useCallback(() => goToPage(page + 1), [goToPage, page]);
    const prev = useCallback(() => goToPage(page - 1), [goToPage, page]);

    useEffect(() => {
        const track = trackRef.current;
        if (!track) return;

        let frame = 0;
        const handleScroll = () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => {
                if (!track.clientWidth) return;
                setPage(Math.round(track.scrollLeft / track.clientWidth));
            });
        };

        track.addEventListener('scroll', handleScroll, { passive: true });
        return () => {
            cancelAnimationFrame(frame);
            track.removeEventListener('scroll', handleScroll);
        };
    }, [itemCount]);

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLElement>) => {
            if (event.key === 'ArrowRight') {
                event.preventDefault();
                next();
                return;
            }
            if (event.key === 'ArrowLeft') {
                event.preventDefault();
                prev();
            }
        },
        [next, prev]
    );

    return {
        trackRef,
        page,
        pageCount,
        itemsPerView,
        canPrev: page > 0,
        canNext: page < pageCount - 1,
        hasOverflow,
        next,
        prev,
        goToPage,
        handleKeyDown,
    };
}
